import { of } from 'rxjs';
import type { RuntimeDataServiceInput, RuntimeDataServiceOutput } from '../contracts';
import { RuntimeDataService } from '../types';
import type { RuntimeDataServiceRequest, RuntimeResultLinkDescriptor } from '../types';
import { isRecord } from '../validationPrimitives';

function validateProjectContextInput(input: unknown): RuntimeDataServiceInput<'project'> {
    if (!isRecord(input)) {
        throw new Error('Expected "project" input to be an object.');
    }

    if (typeof input.name !== 'string') {
        throw new Error('Expected "project.name" to be a string.');
    }

    return {
        name: input.name,
    };
}

function validateProjectContextResult(result: unknown): RuntimeDataServiceOutput<'project'> {
    return validateProjectContextInput(result);
}

export class ProjectContextRuntimeDataService extends RuntimeDataService<'project'> {
    static readonly serviceId = 'project' as const;

    static get serviceMetadata() {
        return {
            label: gettext('DATA_SERVICE_PROJECT_CONTEXT_LABEL'),
            description: gettext('DATA_SERVICE_PROJECT_CONTEXT_DESCRIPTION'),
        } as const;
    }

    static get serviceResultLinks() {
        return [
            {
                key: 'name',
                label: gettext('DATA_SERVICE_PROJECT_CONTEXT_PROJECT_NAME_LABEL'),
                description: gettext('DATA_SERVICE_PROJECT_CONTEXT_PROJECT_NAME_DESCRIPTION'),
                supportedContextKinds: ['project'],
                supportedSwarmTypes: null,
                path: 'name',
                valueKind: 'scalar',
                argPolicy: 'forbidden',
                exposeInInsertionPresets: true,
                buildInput: ({ context }) => {
                    if (!context || context.kind !== 'project') {
                        throw new Error('Project context binding requires project render context.');
                    }

                    return {
                        name: context.project.name,
                    };
                },
            },
        ] as const satisfies readonly RuntimeResultLinkDescriptor<'project'>[];
    }

    validateArgs = validateProjectContextInput;

    validateResult = validateProjectContextResult;

    createResult$ = ({ input }: RuntimeDataServiceRequest<'project'>) => of(input);
}

export function createProjectContextService(): ProjectContextRuntimeDataService {
    return new ProjectContextRuntimeDataService();
}
